import { useCallback, useEffect, useRef, useState } from 'react';
import { getPersona, type Persona } from '../../lib/api/Persona';
import { useUserInfoContext } from '../../lib/contexts/UserInfoContext';

export type LearningWorkspacePersonaState =
  | { status: 'loading' }
  | { status: 'ready'; persona: Persona & { id: string } }
  | { status: 'unavailable' }
  | { status: 'error'; retry: () => void };

function hasPersistedPersonaId(persona: Persona | undefined): persona is Persona & { id: string } {
  return typeof persona?.id === 'string'
    && persona.id.length > 0
    && persona.id.trim() === persona.id;
}

export function useLearningWorkspacePersona(personaId?: string): LearningWorkspacePersonaState {
  const { userInfo } = useUserInfoContext();
  const userInfoRef = useRef(userInfo);
  userInfoRef.current = userInfo;
  const [retryToken, setRetryToken] = useState(0);
  const retry = useCallback(() => {
    setRetryToken((token) => token + 1);
  }, []);
  const [state, setState] = useState<LearningWorkspacePersonaState>(
    personaId ? { status: 'loading' } : { status: 'unavailable' },
  );

  useEffect(() => {
    let isCurrent = true;

    if (!personaId) {
      setState({ status: 'unavailable' });
      return () => {
        isCurrent = false;
      };
    }

    const currentUserInfo = userInfoRef.current;
    if (!currentUserInfo) {
      setState({ status: 'loading' });
      return () => {
        isCurrent = false;
      };
    }

    setState({ status: 'loading' });
    getPersona(currentUserInfo, personaId)
      .then((persona) => {
        if (isCurrent) {
          setState(hasPersistedPersonaId(persona)
            ? { status: 'ready', persona }
            : { status: 'unavailable' });
        }
      })
      .catch(() => {
        if (isCurrent) {
          setState({ status: 'error', retry });
        }
      });

    return () => {
      isCurrent = false;
    };
  // A Cognito token refresh replaces userInfo every five minutes. Reloading the
  // persona for the same user briefly sets this hook to loading, which unmounts
  // an active inline avatar call and ends it. The persona only needs reloading
  // when the authenticated identity, configured persona, or explicit retry changes.
  }, [personaId, retry, retryToken, userInfo?.userId]);

  return state;
}
